import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Sparkles, FileText, Loader2 } from "lucide-react";
import axios from "axios";

const REPORT_TYPES = [
  { value: "riepilogo_mensile", label: "Riepilogo Mensile" },
  { value: "analisi_proprieta", label: "Analisi Proprietà" },
  { value: "previsione", label: "Previsione Guadagni" },
  { value: "suggerimenti", label: "Suggerimenti Strategici" },
];

export default function AIReportsPage() {
  const { user, API } = useAuth();
  const [reportType, setReportType] = useState("riepilogo_mensile");
  const [propertyId, setPropertyId] = useState("all");
  const [properties, setProperties] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/properties`),
      axios.get(`${API}/ai/reports`)
    ]).then(([p, r]) => {
      setProperties(p.data);
      setHistory(r.data);
      if (!isAdmin && r.data.length > 0) setCurrentReport(r.data[0]);
    }).catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [API, isAdmin]);

  const handleGenerate = async () => {
    setGenerating(true);
    setCurrentReport(null);
    try {
      const payload = { report_type: reportType };
      if (propertyId !== "all") payload.property_id = propertyId;
      const res = await axios.post(`${API}/ai/report`, payload);
      setCurrentReport(res.data);
      setHistory(prev => [res.data, ...prev]);
      toast.success("Report generato con successo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella generazione del report");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="ai-reports-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Report AI</h1>
        <p className="text-stone-500 mt-1 text-sm">
          {isAdmin ? "Genera analisi intelligenti delle tue proprietà con AI" : "Consulta i report generati per le tue proprietà"}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          {isAdmin && (
            <Card className="border border-stone-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium text-stone-900 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-600" /> Genera Report
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-stone-500 mb-1.5">Tipo di report</p>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger data-testid="ai-report-type-select" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REPORT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-1.5">Proprietà (opzionale)</p>
                  <Select value={propertyId} onValueChange={setPropertyId}>
                    <SelectTrigger data-testid="ai-report-property-select" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le proprietà</SelectItem>
                      {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="ai-report-generate-button"
                  className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                >
                  {generating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generazione in corso...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" /> Genera Report</>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="border border-stone-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-stone-900">
                {isAdmin ? "Cronologia" : "I tuoi Report"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0" data-testid="ai-report-history">
              {loadingHistory ? (
                <div className="p-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-2 border-stone-300 border-t-stone-900 mx-auto" /></div>
              ) : history.length === 0 ? (
                <div className="p-4 text-center text-stone-400 text-xs">
                  {isAdmin ? "Nessun report generato" : "Nessun report disponibile"}
                </div>
              ) : (
                <div className="divide-y divide-stone-100 max-h-[500px] overflow-y-auto">
                  {history.map((r, i) => (
                    <button
                      key={r.id || i}
                      onClick={() => setCurrentReport(r)}
                      data-testid={`ai-report-history-item-${i}`}
                      className={`w-full text-left px-4 py-3 hover:bg-stone-50 transition-colors ${currentReport?.id === r.id ? "bg-stone-50" : ""}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="h-3.5 w-3.5 text-stone-400" />
                        <span className="text-xs font-medium text-stone-900">
                          {REPORT_TYPES.find(t => t.value === r.report_type)?.label || r.report_type}
                        </span>
                      </div>
                      <p className="text-[10px] text-stone-400 font-mono">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="border border-stone-200 shadow-sm min-h-[500px]">
            <CardHeader className="pb-3 border-b border-stone-100">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium text-stone-900">
                  {currentReport ? (REPORT_TYPES.find(t => t.value === currentReport.report_type)?.label || "Report") : "Report"}
                </CardTitle>
                {currentReport && (
                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                    GPT-5.2
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6" data-testid="ai-report-content">
              {generating ? (
                <div className="flex flex-col items-center justify-center h-80">
                  <Loader2 className="h-8 w-8 text-amber-600 animate-spin mb-4" />
                  <p className="text-sm text-stone-500">Analisi in corso...</p>
                  <p className="text-xs text-stone-400 mt-1">Questo potrebbe richiedere qualche secondo</p>
                </div>
              ) : currentReport ? (
                <div className="prose prose-sm prose-stone max-w-none">
                  <div className="whitespace-pre-wrap text-sm text-stone-700 leading-relaxed">
                    {currentReport.content}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-80 text-center">
                  <Sparkles className="h-12 w-12 text-stone-200 mb-4" />
                  <p className="text-stone-500 text-sm">
                    {isAdmin ? "Seleziona il tipo di report e clicca \"Genera\"" : "Seleziona un report dalla lista per visualizzarlo"}
                  </p>
                  <p className="text-stone-400 text-xs mt-1">
                    {isAdmin ? "L'AI analizzerà i tuoi dati e genererà un report dettagliato" : "I report vengono generati dall'amministratore"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
